import React from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import logo from 'resources/images/logo.webp'
import homeImage from 'resources/images/Banner1.jpg'
import aboutImage from 'resources/images/goldBanner.jpg'
import servicesImage from 'resources/images/servicesBanner.jpg'
import strengthImage from 'resources/images/ourStrengthBg.jpg'
import contactImage from 'resources/images/contactUsBanner.jpg'

const SITE_URL = 'https://www.sinoasiabnr.com'
const CONTACT_EMAIL = 'master@sinoasiabnr.com'
const CONTACT_PHONE = '+852-3621 0366'
const COMPANY_NAME = {
    en: 'Sinoasia Belt & Road Limited',
    zh: '中国汇智带路有限公司',
}

const PAGE_META = {
    '/': {
        schemaType: 'WebPage',
        image: homeImage,
        breadcrumb: null,
        title: {
            en: 'Cross-Border Trading Solutions & Strategic Consultancy | Sinoasia Belt & Road Limited',
            zh: '跨境贸易解决方案与战略咨询 | 中国汇智带路有限公司',
        },
        description: {
            en: 'Sinoasia Belt & Road Limited provides financial structuring, cross-border trading solutions, gold trade registration support, and strategic consultancy for Chinese interests expanding abroad.',
            zh: '中国汇智带路有限公司专注为中国企业出海提供金融架构设计、跨境贸易解决方案、黄金贸易登记相关服务及“一带一路”市场战略咨询。',
        },
    },
    '/about-us': {
        schemaType: 'AboutPage',
        image: aboutImage,
        breadcrumb: {
            en: 'About Us',
            zh: '关于我们',
        },
        title: {
            en: 'About Us | Sinoasia Belt & Road Limited',
            zh: '关于我们 | 中国汇智带路有限公司',
        },
        description: {
            en: "Learn about Sinoasia Belt & Road Limited, our Hong Kong precious metals registration, management team, and our vision for supporting Chinese enterprises in Belt and Road markets.",
            zh: '了解中国汇智带路有限公司的发展背景、香港贵金属及宝石登记资质、管理团队与面向“一带一路”市场的战略愿景。',
        },
    },
    '/our-services': {
        schemaType: 'WebPage',
        image: servicesImage,
        breadcrumb: {
            en: 'Our Services',
            zh: '我们的服务',
        },
        title: {
            en: 'Our Services | Sinoasia Belt & Road Limited',
            zh: '我们的服务 | 中国汇智带路有限公司',
        },
        description: {
            en: 'Explore Sinoasia services across cross-border trade solutions, global insurance networks, risk structuring, compliance support, and advisory services for Belt and Road projects.',
            zh: '了解中国汇智带路有限公司在跨境贸易、全球保险网络、风险架构、合规支持及“一带一路”项目咨询方面的服务能力。',
        },
    },
    '/our-strength': {
        schemaType: 'WebPage',
        image: strengthImage,
        breadcrumb: {
            en: 'Our Strength',
            zh: '我们的优势',
        },
        title: {
            en: 'Our Strength | Sinoasia Belt & Road Limited',
            zh: '我们的优势 | 中国汇智带路有限公司',
        },
        description: {
            en: 'See how Sinoasia delivers focused, compliant, and client-centered risk management and advisory support with multilingual teams across key overseas markets.',
            zh: '了解中国汇智带路有限公司如何以专注、合规、交付和客户关怀为核心，为企业提供多区域、多语言的风险管理与咨询支持。',
        },
    },
    '/contact-us': {
        schemaType: 'ContactPage',
        image: contactImage,
        breadcrumb: {
            en: 'Contact Us',
            zh: '联系我们',
        },
        title: {
            en: 'Contact Us | Sinoasia Belt & Road Limited',
            zh: '联系我们 | 中国汇智带路有限公司',
        },
        description: {
            en: 'Contact Sinoasia Belt & Road Limited in Hong Kong for cross-border trade, risk management, insurance, and strategic consultancy enquiries.',
            zh: '联系中国汇智带路有限公司香港团队，咨询跨境贸易、风险管理、保险安排及战略咨询服务。',
        },
    },
}

function normalizePathname(pathname) {
    if (!pathname || pathname === '/') {
        return '/'
    }

    const trimmedPath = pathname.replace(/\/+$/, '')

    return trimmedPath === '/home' ? '/' : trimmedPath
}

function getLanguageConfig(language) {
    if (language === 'zh') {
        return {
            key: 'zh',
            htmlLang: 'zh-CN',
            ogLocale: 'zh_CN',
        }
    }

    return {
        key: 'en',
        htmlLang: 'en',
        ogLocale: 'en_HK',
    }
}

function buildPageUrl(pathname, language) {
    const url = new URL(pathname, SITE_URL)

    if (language === 'zh') {
        url.searchParams.set('lang', 'zh')
    }

    return url.toString()
}

function makeAbsoluteAssetUrl(assetPath) {
    return new URL(assetPath, SITE_URL).toString()
}

export default function Seo() {
    const location = useLocation()
    const {
        i18n: { language },
    } = useTranslation()

    const languageConfig = getLanguageConfig(language)
    const pathname = normalizePathname(location.pathname)
    const pageMeta = PAGE_META[pathname] ?? PAGE_META['/']
    const currentLanguage = languageConfig.key
    const pageTitle = pageMeta.title[currentLanguage]
    const pageDescription = pageMeta.description[currentLanguage]
    const canonicalUrl = buildPageUrl(pathname, currentLanguage)
    const englishUrl = buildPageUrl(pathname, 'en')
    const chineseUrl = buildPageUrl(pathname, 'zh')
    const socialImage = makeAbsoluteAssetUrl(pageMeta.image)
    const companyName = COMPANY_NAME[currentLanguage]

    const organizationSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: companyName,
        url: buildPageUrl('/', currentLanguage),
        logo: makeAbsoluteAssetUrl(logo),
        email: CONTACT_EMAIL,
        telephone: CONTACT_PHONE,
        address: {
            '@type': 'PostalAddress',
            streetAddress: 'Room 2002, 20/F, Greenfield Tower, Concordia Plaza, 1 Science Museum Road, TST',
            addressLocality: 'Kowloon',
            addressRegion: 'Hong Kong',
            addressCountry: 'HK',
        },
    }

    const webSiteSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: companyName,
        url: buildPageUrl('/', currentLanguage),
        inLanguage: languageConfig.htmlLang,
    }

    const pageSchema = {
        '@context': 'https://schema.org',
        '@type': pageMeta.schemaType,
        name: pageTitle,
        description: pageDescription,
        url: canonicalUrl,
        inLanguage: languageConfig.htmlLang,
        isPartOf: {
            '@type': 'WebSite',
            name: companyName,
            url: buildPageUrl('/', currentLanguage),
        },
        primaryImageOfPage: {
            '@type': 'ImageObject',
            url: socialImage,
        },
    }

    const breadcrumbSchema = pageMeta.breadcrumb
        ? {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                  {
                      '@type': 'ListItem',
                      position: 1,
                      name: COMPANY_NAME[currentLanguage],
                      item: buildPageUrl('/', currentLanguage),
                  },
                  {
                      '@type': 'ListItem',
                      position: 2,
                      name: pageMeta.breadcrumb[currentLanguage],
                      item: canonicalUrl,
                  },
              ],
          }
        : null

    return (
        <Helmet htmlAttributes={{ lang: languageConfig.htmlLang }}>
            <title>{pageTitle}</title>
            <meta name='description' content={pageDescription} />
            <meta
                name='robots'
                content='index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
            />
            <link rel='canonical' href={canonicalUrl} />
            <link rel='alternate' hrefLang='en' href={englishUrl} />
            <link rel='alternate' hrefLang='zh-CN' href={chineseUrl} />
            <link rel='alternate' hrefLang='x-default' href={englishUrl} />
            <meta property='og:site_name' content={companyName} />
            <meta property='og:type' content='website' />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={pageDescription} />
            <meta property='og:url' content={canonicalUrl} />
            <meta property='og:locale' content={languageConfig.ogLocale} />
            <meta property='og:locale:alternate' content={currentLanguage === 'zh' ? 'en_HK' : 'zh_CN'} />
            <meta property='og:image' content={socialImage} />
            <meta property='og:image:alt' content={pageTitle} />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={pageDescription} />
            <meta name='twitter:image' content={socialImage} />
            <script type='application/ld+json'>{JSON.stringify(organizationSchema)}</script>
            <script type='application/ld+json'>{JSON.stringify(webSiteSchema)}</script>
            <script type='application/ld+json'>{JSON.stringify(pageSchema)}</script>
            {breadcrumbSchema ? (
                <script type='application/ld+json'>{JSON.stringify(breadcrumbSchema)}</script>
            ) : null}
        </Helmet>
    )
}
